import { create } from "zustand";
import { isNumberedAnalysis } from "./sample-number";

export interface CapLabel {
  /** The request number: what goes on the cap. */
  id: number;
  sample: string;
  /** The sample's code, when labels alone repeat (every arm's "Homogenate"). */
  sampleCode?: string | null;
  test: string;
}

interface CapLabelState {
  labels: CapLabel[];
  close: () => void;
  add: (labels: CapLabel[]) => void;
}

/**
 * The cap-label popup. Anything that just requested a numbered analysis
 * (biogenic amines, extracellular amines, LAL) — a test added to a harvest
 * sample, a sample planned with one, a test requested on a fermentation
 * timepoint — hands its numbers here; one popup, mounted at the root, asks the
 * requester to label the cap while the tube is still in hand. Labels arriving
 * while it is open join the same list.
 */
export const useCapLabelStore = create<CapLabelState>((set) => ({
  labels: [],
  close: () => set({ labels: [] }),
  add: (labels) =>
    set((state) => ({
      labels: [
        ...state.labels,
        ...labels.filter(
          (label) => !state.labels.some((existing) => existing.id === label.id),
        ),
      ],
    })),
}));

/**
 * Show the cap popup for whichever of these tests are numbered analyses;
 * a no-op when none are.
 */
export function showCapLabels(
  tests: {
    id: number;
    code: string | null;
    test: string;
    sample: string;
    sampleCode?: string | null;
  }[],
) {
  const numbered = tests.filter((t) => isNumberedAnalysis(t.code));
  if (numbered.length === 0) return;
  useCapLabelStore.getState().add(
    numbered.map(({ id, sample, sampleCode, test }) => ({
      id,
      sample,
      sampleCode: sampleCode ?? null,
      test,
    })),
  );
}
